import React from "react";
import { Languages } from "lucide-react";

import { Button } from "../ui/button";
import { LanguagePicker } from "./LanguagePicker";
import { QUOTE_LANGUAGES, DEFAULT_QUOTE_LANGUAGE } from "../../constants/languages";

interface TranslateQuotesButtonProps {
  value: string;
  onChange: (languageCode: string) => void;
  // Icon-only, for the native app's tight toolbar.
  compact?: boolean;
}

// Bulk "Translate Quotes" action - switches every quote card on the page to
// the chosen language at once, on top of each card's own per-quote language
// menu. Shared across every page that renders a FeedList of quotes (home,
// liked, user profiles). Offers every Google Translate language, searchable.
export function TranslateQuotesButton({ value, onChange, compact = false }: TranslateQuotesButtonProps) {
  const isTranslated = value !== DEFAULT_QUOTE_LANGUAGE;
  const activeLabel = QUOTE_LANGUAGES.find((lang) => lang.code === value)?.label ?? "English";

  return (
    <LanguagePicker value={value} onSelect={onChange} align="end">
      <Button
        variant="ghost"
        size="sm"
        aria-label={isTranslated ? `Quotes in ${activeLabel}. Change language` : "Translate quotes"}
        className={`flex items-center gap-1.5 shrink-0 rounded-full whitespace-nowrap transition-all duration-200 ${compact ? "h-10 w-10 justify-center p-0" : "px-3.5 lg:px-2.5"} ${
          isTranslated
            ? "bg-slate-900 text-white shadow-sm shadow-slate-900/20 hover:bg-slate-900 hover:text-white dark:bg-white dark:text-slate-900 dark:hover:bg-white dark:hover:text-slate-900"
            : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-600 dark:bg-slate-800/80 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        }`}
      >
        <Languages className={compact ? "w-[18px] h-[18px]" : "w-3.5 h-3.5"} />
        <span className={compact ? "sr-only" : "text-xs font-medium"}>{isTranslated ? activeLabel : "Translate Quotes"}</span>
      </Button>
    </LanguagePicker>
  );
}
